import {
  LEAVE_DECISION_EVENT,
  REQUEST_LEAVE_EVENT,
} from '@servicetitan/contactcenter-agent-inbox-mfe-loader';
import type { LeaveDecisionPayload } from '@servicetitan/contactcenter-agent-inbox-mfe-loader';
import type { EventBus } from '@servicetitan/web-components';

export type LeaveDecisionHandlers = {
  onProceed: () => void;
  onAbort: () => void;
};

/**
 * Host half of customer-mfe's leave handshake: ask the MFE for permission to
 * leave via `request-leave`; it shows its discard confirmation (or answers at
 * once when no booking is open) and replies with `leave-decision`.
 *
 * The `leave-decision` listener is attached BEFORE `request-leave` is emitted,
 * because an unguarded MFE answers synchronously from inside the emit.
 *
 * @returns a dispose function that drops the listener without calling either
 *   callback. It is a no-op once a decision has arrived.
 */
export function requestLeave(
  bus: EventBus,
  { onProceed, onAbort }: LeaveDecisionHandlers,
): () => void {
  const handleDecision = (decision: LeaveDecisionPayload) => {
    bus.off(LEAVE_DECISION_EVENT, handleDecision);
    const handle = decision.confirmed ? onProceed : onAbort;
    handle();
  };
  const dispose = () => bus.off(LEAVE_DECISION_EVENT, handleDecision);
  bus.on(LEAVE_DECISION_EVENT, handleDecision);
  try {
    bus.emit(REQUEST_LEAVE_EVENT);
  } catch (error) {
    // the caller never receives the dispose function, so drop the listener here
    dispose();
    throw error;
  }
  return dispose;
}
